import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import { sourceIcon } from '../../utils/sourceIcon';
import { Rating } from 'react-simple-star-rating';
import getTimeAgo from '../../utils/getDateTime';
import { useAuthContext } from '../../utils/context/authContext';

interface IProps {
  data: any;
  setShowModal: (show: boolean) => void;
  productName?: string;
  randomName?: string;
}

const DetailedReviewModal = ({
  data,
  setShowModal,
  productName,
  randomName
}: IProps) => {
  const modalRef: any = useRef(null);
  const { user }: any = useAuthContext();

  const handleCloseModal = (e: any) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      setShowModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleCloseModal);
    return () => {
      document.removeEventListener('mousedown', handleCloseModal);
    };
  }, []);

  function sliceTextToLimitChars(text: string) {
    const maxLength = 900;

    if (text.length <= maxLength) {
      return text;
    }

    const slicedText = text.slice(0, maxLength);
    const lastDotIndex = slicedText.lastIndexOf('.');

    if (lastDotIndex !== -1) {
      return text.slice(0, lastDotIndex + 1);
    }

    return slicedText;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div>
      <div className="fixed z-[1250] top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center">
        <div ref={modalRef} className="bg-white rounded-md w-[90%] md:w-[40%] ">
          <div className="rounded-full flex justify-between px-5 pt-4">
            <div className="flex gap-3">
              <Image
                src={
                  data.author_image || data.image || data.profile_photo_url
                    ? data.author_image || data.image || data.profile_photo_url
                    : '/default-profile-icon.svg'
                }
                width={
                  data.author_image || data.image || data.profile_photo_url
                    ? 40
                    : 60
                }
                height={
                  data.author_image || data.image || data.profile_photo_url
                    ? 40
                    : 60
                }
                alt="review source"
                className={`w-[40px] h-[40px] rounded-full object-cover ${
                  data.author_image ? 'mt-5' : 'mt-0'
                }`}
              />
              <div className="flex flex-col">
                {(data.author || data.author_name || data.name) && (
                  <p className="text-base lg:text-lg font-semibold leading-[18px]">
                    {data.author || data.author_name || data.name}{' '}
                  </p>
                )}
                {(data.traveller || randomName) && (
                  <p className="text-sm sm:text-base sm:leading-4 leading-3 xl:text-lg font-semibold mt-[5px] xl:leading-4">
                    {data.traveller === null ? randomName : data.traveller}
                  </p>
                )}
                <Rating
                  initialValue={Number(data?.rating)}
                  disableFillHover={true}
                  allowFraction={true}
                  fillColor="orange"
                  emptyColor="#9B9B9B"
                  readonly={true}
                  SVGstyle={{
                    display: 'inline-block'
                  }}
                  size={15}
                />
              </div>
            </div>
            <div className="cursor-pointer" onClick={() => setShowModal(false)}>
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.398438"
                  y="0.5"
                  width="20.5989"
                  height="20"
                  rx="10"
                  fill="#DEDEDE"
                />
                <path
                  d="M14.7681 7.51358C14.8398 7.44442 14.897 7.36169 14.9363 7.27021C14.9757 7.17872 14.9964 7.08032 14.9973 6.98074C14.9982 6.88115 14.9793 6.78238 14.9416 6.69019C14.904 6.598 14.8483 6.51424 14.7779 6.44379C14.7076 6.37333 14.6238 6.31761 14.5317 6.27985C14.4395 6.2421 14.3408 6.22307 14.2412 6.22389C14.1416 6.22471 14.0432 6.24535 13.9517 6.28462C13.8602 6.32388 13.7774 6.38098 13.7081 6.45258L10.7021 9.45758L7.69715 6.45258C7.62848 6.37889 7.54568 6.31979 7.45368 6.27879C7.36168 6.2378 7.26237 6.21576 7.16167 6.21398C7.06097 6.21221 6.96094 6.23073 6.86755 6.26845C6.77416 6.30617 6.68933 6.36232 6.61811 6.43354C6.54689 6.50476 6.49074 6.58959 6.45302 6.68298C6.4153 6.77637 6.39678 6.8764 6.39855 6.9771C6.40033 7.0778 6.42237 7.17711 6.46336 7.26911C6.50436 7.36111 6.56346 7.44391 6.63715 7.51258L9.64015 10.5186L6.63515 13.5236C6.50267 13.6658 6.43054 13.8538 6.43397 14.0481C6.4374 14.2424 6.51611 14.4278 6.65352 14.5652C6.79094 14.7026 6.97632 14.7813 7.17062 14.7847C7.36492 14.7882 7.55297 14.7161 7.69515 14.5836L10.7021 11.5786L13.7071 14.5846C13.8493 14.7171 14.0374 14.7892 14.2317 14.7858C14.426 14.7823 14.6114 14.7036 14.7488 14.5662C14.8862 14.4288 14.9649 14.2434 14.9683 14.0491C14.9717 13.8548 14.8996 13.6667 14.7671 13.5246L11.7641 10.5186L14.7681 7.51358Z"
                  fill="black"
                />
              </svg>
            </div>
          </div>
          <div className="flex flex-col relative py-3 px-5 rounded-md h-full">
            <div>
              {productName !== undefined && (
                <p className="text-base lg:text-lg font-semibold mt-2">
                  {productName?.length > 40
                    ? `${productName?.slice(0, 40)}...`
                    : productName}
                </p>
              )}
              <p className="text-sm lg:text-base font-light text-justify mt-2">
                {sliceTextToLimitChars(
                  data?.review ? data?.review : data?.text
                )}
              </p>
            </div>
            <div className="flex justify-end mt-2 items-center text-sm md:text-lg">
              {formatDate(data?.time || data?.created_at || data?.published_at)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedReviewModal;
